import { LockOutlined, MailOutlined } from '@ant-design/icons';
import { Button, Form, Input } from 'antd';
import cls from 'clsx';
import { useEffect, useState } from 'react';
import type { ChangeEvent, FocusEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import type { RememberedCredentials } from '@/entities/session';
import type { ApiError } from '@/shared/api/types';
import EyeInvisibleIcon from '@/shared/assets/icons/eye-invisible.svg?react';
import EyeIcon from '@/shared/assets/icons/eye.svg?react';
import { Checkbox } from '@/shared/ui/Checkbox';

import s from '@/shared/ui/AuthForm.module.css';

interface LoginFormValues {
  email: string;
  password: string;
}

interface LoginFormProps {
  // The remembered email, or `null` when "Remember Me" is off
  rememberMe?: RememberedCredentials | null;
  setRemember: (values: RememberedCredentials | null) => void;
  onSubmit: (values: LoginFormValues) => void;
  // The failed request, as `parseApiError` returns it
  error?: ApiError | null;
  children?: ReactNode;
}

export function LoginForm({
  rememberMe,
  setRemember,
  onSubmit,
  error,
  ...props
}: LoginFormProps) {
  const [errorText, setErrorText] = useState<ReactNode>(null);
  const [isBackError, setIsBackError] = useState(false);

  const [form] = Form.useForm<LoginFormValues>();
  const { t } = useTranslation();

  useEffect(() => {
    if (error) {
      setIsBackError(true);
      setErrorText(error.message);
    }
  }, [error]);

  const onFinish = (values: LoginFormValues) => {
    if (rememberMe) setRemember({ email: values.email });
    onSubmit(values);
  };

  const onFinishFailed = ({
    errorFields,
  }: {
    errorFields?: { errors: string[] }[];
  }) => {
    if (errorFields) setErrorText(errorFields[0]?.errors);
  };

  function onChange(e: ChangeEvent<HTMLInputElement>) {
    if (e.target.checked) {
      setRemember({ email: form.getFieldValue('email') });
    } else {
      setRemember(null);
    }
  }

  function handleClearError(_e: unknown, field: keyof LoginFormValues) {
    form.setFields([
      {
        name: field,
        errors: [],
      },
    ]);
    setErrorText(null);
    setIsBackError(false);
  }

  function onBlurHandler(e: FocusEvent<HTMLInputElement>) {
    const target = e.target;

    setTimeout(() => {
      const error = form.getFieldError(target.name as keyof LoginFormValues);
      if (error.length) {
        setErrorText(error[0]);
      }
    }, 10);
  }

  return (
    <Form
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      validateTrigger="onSubmit"
      className={s.form}
      layout="vertical"
      form={form}
    >
      <Form.Item noStyle shouldUpdate={true}>
        {() => (
          <div className={cls(s.errorText, !errorText && s.errorTextEmpty)}>
            {errorText}
          </div>
        )}
      </Form.Item>

      <Form.Item
        label=""
        name="email"
        initialValue={rememberMe?.email}
        validateStatus={errorText || isBackError ? 'error' : ''}
        rules={[
          {
            type: 'email',
            message: t('The email or password you entered is incorrect.'),
          },
          {
            required: true,
            message: t('The email or password you entered is incorrect.'),
          },
        ]}
      >
        <Input
          placeholder={t('Email Address')}
          name="email"
          prefix={<MailOutlined className={s.inputIcon} />}
          onChange={(e) => handleClearError(e, 'email')}
          onBlur={onBlurHandler}
        />
      </Form.Item>
      <Form.Item
        label=""
        name="password"
        validateStatus={errorText || isBackError ? 'error' : ''}
        rules={[
          {
            required: true,
            message: t('The email or password you entered is incorrect.'),
          },
        ]}
      >
        <Input.Password
          size="large"
          placeholder={t('Password')}
          autoComplete="new-password"
          visibilityToggle
          prefix={<LockOutlined className={s.inputIcon} />}
          onChange={(e) => handleClearError(e, 'password')}
          iconRender={(visible) =>
            visible ? (
              <EyeIcon stroke="var(--ant-color-primary)" />
            ) : (
              <EyeInvisibleIcon stroke="var(--ant-color-primary-border)" />
            )
          }
        />
      </Form.Item>

      <Button type="primary" htmlType="submit" className={s.submit}>
        {t('Sign In')}
      </Button>

      <div className={s.formFooter}>
        <Checkbox type={'path'} checked={!!rememberMe} onChange={onChange}>
          {t('Remember Me')}
        </Checkbox>
        <Link to="/restore-password">{t('Forgot Password?')}</Link>
      </div>

      {props.children}
    </Form>
  );
}
